import { useState } from "react";
import { FileInput, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const stamp = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
}) : "");

/**
 * Import history behind one icon. Highlighted when this estimate has live imports; the file
 * list and dates sit in a popover so the toolbar stays a single row.
 */
export default function ImportHistoryButton({ batches, onUndo }) {
  const [open, setOpen] = useState(false);
  const live = (batches || []).filter((b) => !b.undone_at);
  const has = live.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Deliberately unhighlighted: it is here to check against, not to be noticed. */}
        <Button variant="outline" size="icon" className="relative h-8 w-8 text-slate-500"
                onMouseEnter={() => has && setOpen(true)}
                title={has ? `${live.length} import(s) on this estimate` : "No imports yet"}
                data-testid="import-history-btn">
          <FileInput className="h-3.5 w-3.5" />
          {has && (
            <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-slate-200 px-1 text-[9px] font-medium text-slate-600"
                  data-testid="import-history-count">
              {live.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" onMouseLeave={() => setOpen(false)}
                      data-testid="import-history-card">
        <div className="border-b border-slate-100 px-3 py-2">
          <p className="text-xs font-semibold text-slate-800">Imports on this estimate</p>
          <p className="text-[10px] text-slate-400">
            Each import is one undoable batch of lines.
          </p>
        </div>
        {!has ? (
          <p className="px-3 py-6 text-center text-xs text-slate-400" data-testid="import-history-empty">
            Nothing imported yet. Use Takeoff to bring in an FRS or a takeoff spreadsheet.
          </p>
        ) : (
          <ul className="max-h-64 overflow-auto">
            {live.map((b) => (
              <li key={b.batch_id}
                  className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-0"
                  data-testid={`import-batch-${b.batch_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-slate-700" title={b.file_name || b.kind}>
                    {b.file_name || b.kind}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {[`${b.counts?.added ?? 0} added`,
                      b.counts?.qty_updated ? `${b.counts.qty_updated} qty changed` : "",
                      b.counts?.unchanged ? `${b.counts.unchanged} unchanged` : "",
                      b.counts?.dropped_flagged ? `${b.counts.dropped_flagged} dropped` : "",
                      stamp(b.created_at)].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <button className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                        title="Undo this import" onClick={() => onUndo(b.batch_id)}
                        data-testid={`import-undo-${b.batch_id}`}>
                  <Undo2 className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
